import {
  isColorRangeFormatting,
  type ColorRangeFormatting,
  type FormattingOperator,
  type QueryBuilderRuleValue,
} from '@ardoq/api-types';
import { DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import {
  getColorRangeModeLimits,
  getColorRangePointError,
} from '@ardoq/perspectives';
import {
  getNumberValueKind,
  isWholeNumberFilter,
  NumberValueKind,
} from '@ardoq/query-builder';

type NumberCheck = {
  isValid: (value: unknown) => boolean;
  noun: string;
};

const DECIMAL_NUMBER = /^\s*[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?\s*$/i;

const isFiniteNumberLike = (value: unknown): boolean =>
  typeof value === 'number'
    ? Number.isFinite(value)
    : typeof value === 'string' &&
      DECIMAL_NUMBER.test(value) &&
      Number.isFinite(Number(value));

const isWholeNumberLike = (value: unknown): boolean =>
  isFiniteNumberLike(value) && Number.isInteger(Number(value));

const FINITE_NUMBER_CHECK: NumberCheck = {
  isValid: isFiniteNumberLike,
  noun: 'finite number',
};

const WHOLE_NUMBER_CHECK: NumberCheck = {
  isValid: isWholeNumberLike,
  noun: 'whole number',
};

const isNumberPair = (
  value: QueryBuilderRuleValue | undefined,
  { isValid }: NumberCheck
): boolean =>
  Array.isArray(value) && value.length === 2 && value.every(isValid);

/** What a numeric comparator on this field needs its value to be, or `null` when the value fits. */
export const describeExpectedNumberValue = (
  fieldName: string,
  comparator: FormattingOperator,
  value: QueryBuilderRuleValue | undefined
): string | null => {
  const kind = getNumberValueKind(comparator);
  if (
    kind === null ||
    kind === NumberValueKind.NONE ||
    kind === NumberValueKind.COLOR_RANGE
  ) {
    return null;
  }

  const check = isWholeNumberFilter(fieldName)
    ? WHOLE_NUMBER_CHECK
    : FINITE_NUMBER_CHECK;

  if (kind === NumberValueKind.PAIR) {
    return isNumberPair(value, check)
      ? null
      : `an array of two ${check.noun}s, [from, to], such as [1, 10]`;
  }

  return check.isValid(value) ? null : `a ${check.noun}, such as 5`;
};

const COLOR_RANGE_POINT_KEYS = ['min', 'mid', 'max'] as const;

const describePointOutsideModeLimits = ({
  mode,
  ...points
}: ColorRangeFormatting): string | null => {
  const { min: lowest, max: highest } = getColorRangeModeLimits(mode);
  const pointKey = COLOR_RANGE_POINT_KEYS.find(key => {
    const pointValue = points[key].value;
    return (
      typeof pointValue === 'number' &&
      (pointValue < lowest || pointValue > highest)
    );
  });

  return pointKey === undefined
    ? null
    : `the ${pointKey} value (${points[pointKey].value}) is outside the ${mode} limits of ${lowest} to ${highest}`;
};

/** Why a colour range is not valid in the rule editor, or `null` when it is. */
export const describeInvalidColorRange = (
  comparator: FormattingOperator,
  value: QueryBuilderRuleValue | undefined
): string | null => {
  if (getNumberValueKind(comparator) !== NumberValueKind.COLOR_RANGE) {
    return null;
  }
  if (!isColorRangeFormatting(value)) {
    return null;
  }

  return (
    describePointOutsideModeLimits(value) ??
    getColorRangePointError(DEFAULT_ARDOQ_I18NLOCALE, value)?.message ??
    null
  );
};

/**
 * The number the filters evaluate a numeric rule value as. Accepted numeric
 * strings become numbers, so the stored rule matches what the rule editor
 * stores and what the overlap warnings read.
 */
export const toNumberValue = (
  comparator: FormattingOperator | undefined,
  value: QueryBuilderRuleValue
): QueryBuilderRuleValue => {
  const kind = getNumberValueKind(comparator);

  if (kind === NumberValueKind.SINGLE && typeof value === 'string') {
    return Number(value);
  }
  if (kind === NumberValueKind.PAIR && Array.isArray(value)) {
    const [from, to] = value.map(Number);
    return [from, to];
  }
  return value;
};
